import isNil from 'lodash/isNil'
import { PersonRole } from '../../../../types/person'

// RBAC role display names as seeded in `authz.role.name`. Kept as an enum so the
// assignable-role mapping below references a single source of truth instead of
// scattered magic strings.
export enum RbacRoleName {
  ACCOUNT_MANAGER = 'Account Manager',
  FINANCE_ADMIN = 'Finance Admin',
  RBAC_ADMIN = 'RBAC Admin',
  FINANCE_MANAGER = 'Finance Manager',
  HR_ADMIN = 'HR Admin',
  PLATFORM_ADMIN = 'Platform Admin',
  EMPLOYEE = 'Employee',
  US_RESTRICTED_DATA_ACCESS = 'US-Restricted Data Access',
}

// Which RBAC roles each Cognito `custom:user-role` is allowed to assign in the
// "+ Add role" dropdown. Any Cognito role not listed here can assign nothing.
const ASSIGNABLE_ROLES_BY_COGNITO_ROLE: Readonly<
  Partial<Record<PersonRole, readonly RbacRoleName[]>>
> = {
  [PersonRole.PlatformHixmeAdmin]: [
    RbacRoleName.ACCOUNT_MANAGER,
    RbacRoleName.FINANCE_ADMIN,
    RbacRoleName.RBAC_ADMIN,
    RbacRoleName.PLATFORM_ADMIN,
    RbacRoleName.EMPLOYEE,
    RbacRoleName.US_RESTRICTED_DATA_ACCESS,
  ],
  [PersonRole.PlatformClientAdmin]: [
    RbacRoleName.FINANCE_MANAGER,
    RbacRoleName.HR_ADMIN,
    RbacRoleName.EMPLOYEE,
  ],
}

/**
 * Returns the set of RBAC role names the given Cognito role may assign. Returns
 * an empty set when the role is nil or has no mapping, so unmapped admins get an
 * empty "+ Add role" dropdown.
 */
export function getAssignableRoleNames(cognitoRole?: unknown): Set<string> {
  if (isNil(cognitoRole) || typeof cognitoRole !== 'string') return new Set()
  const allowed = ASSIGNABLE_ROLES_BY_COGNITO_ROLE[cognitoRole as PersonRole]
  return new Set(allowed ?? [])
}

// The RBAC role a Cognito role implicitly grants when the user has no explicitly
// granted role. Mirrors the backend's env-configured implication so the drawer
// can preview the fallback when all roles are removed locally.
const IMPLIED_ROLE_NAME_BY_COGNITO_ROLE: Readonly<
  Partial<Record<PersonRole, RbacRoleName>>
> = {
  [PersonRole.PlatformClientAdmin]: RbacRoleName.HR_ADMIN,
  [PersonRole.PlatformHixmeAdmin]: RbacRoleName.PLATFORM_ADMIN,
}

/**
 * Returns the RBAC role name the given Cognito role implies, or undefined when
 * the role is nil or unmapped.
 */
export function getImpliedRoleName(cognitoRole?: unknown): string | undefined {
  if (isNil(cognitoRole) || typeof cognitoRole !== 'string') return undefined
  return IMPLIED_ROLE_NAME_BY_COGNITO_ROLE[cognitoRole as PersonRole]
}
